import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import {
  AutocompleteInputComponent,
  AutocompleteItem,
} from '@delmonicos/shared-design-system-react/src/components/autocomplete-input/autocomplete-input.component';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Checkbox } from '@delmonicos/shared-design-system-react/src/components/checkbox/checkbox';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Text } from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { useCreateChargePointFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-charge-point.form-validation';
import { CreateChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-charge-point.model';
import {
  EvseCapability,
  ParkingRestriction,
} from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import { useEffect, useState } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCpmsBoGetLocationsPaginatedQuery } from '@app/core/service/cpms-bo-location.service';
import { useCpmsBoGetTariffsPaginatedQuery } from '@app/core/service/cpms-bo-tariff.service';

import { useCreateChargePointContext } from '../../charge-point.context';
import * as css from './charge-point-create-form-step-3.module.scss';

const ChargePointCreateFormStep3ParkingRestrictionsSelection =
  (): React.JSX.Element => {
    // CONTEXT ///////////////////////////////////////////////////////////////////

    const { t } = useTranslation();
    const { control, setValue, watch } =
      useFormContext<CreateChargePointModel>();
    const { parkingRestrictions } = useCreateChargePointFormValidation();

    // WATCHERS //////////////////////////////////////////////////////////////////

    const parkingRestrictionsWatcher = watch('parkingRestrictions');

    // HANDLERS //////////////////////////////////////////////////////////////////

    const handleParkingRestrictionsChange = (
      parkingRestrictionChanged: ParkingRestriction,
    ) => {
      if (!parkingRestrictionsWatcher) {
        setValue('parkingRestrictions', [parkingRestrictionChanged], {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        return;
      }

      setValue(
        'parkingRestrictions',
        parkingRestrictionsWatcher.includes(parkingRestrictionChanged)
          ? parkingRestrictionsWatcher.filter(
              (parkingRestrictionCurrent) =>
                parkingRestrictionCurrent !== parkingRestrictionChanged,
            )
          : [...parkingRestrictionsWatcher, parkingRestrictionChanged],
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        },
      );
    };

    // RENDER ////////////////////////////////////////////////////////////////////

    return (
      <Controller
        name="parkingRestrictions"
        control={control}
        rules={parkingRestrictions}
        render={({ field }) => {
          // TODO: Should be set in SDK as a list prepared list
          const parkingRestrictionList = [
            ParkingRestriction.CUSTOMERS,
            ParkingRestriction.EV_ONLY,
            ParkingRestriction.DISABLED,
            ParkingRestriction.PLUGGED,
            ParkingRestriction.MOTORCYCLES,
          ];

          return (
            <>
              {parkingRestrictionList.map((parkingRestrictionCurrent) => {
                return (
                  <Checkbox
                    key={parkingRestrictionCurrent}
                    id={parkingRestrictionCurrent}
                    label={t(
                      `delmonicos-shared-sdk.ocpi-protocol-enums.parking-restriction.${parkingRestrictionCurrent}`,
                    )}
                    checked={
                      !field.value
                        ? false
                        : field.value.includes(parkingRestrictionCurrent)
                    }
                    onChange={() =>
                      handleParkingRestrictionsChange(parkingRestrictionCurrent)
                    }
                  />
                );
              })}
            </>
          );
        }}
      />
    );
  };

const ChargePointCreateFormStep3CapabilitiesSelection =
  (): React.JSX.Element => {
    // CONTEXT ///////////////////////////////////////////////////////////////////

    const { t } = useTranslation();
    const { control, setValue, watch } =
      useFormContext<CreateChargePointModel>();
    const { capabilities } = useCreateChargePointFormValidation();

    // WATCHERS //////////////////////////////////////////////////////////////////

    const capabilitiesWatcher = watch('capabilities');

    // HANDLERS //////////////////////////////////////////////////////////////////

    const handleCapibilitiesChange = (capabilitiesChanged: EvseCapability) => {
      if (!capabilitiesWatcher) {
        setValue('capabilities', [capabilitiesChanged]);
        return;
      }

      setValue(
        'capabilities',
        capabilitiesWatcher.includes(capabilitiesChanged)
          ? capabilitiesWatcher.filter(
              (parkingRestrictionCurrent) =>
                parkingRestrictionCurrent !== capabilitiesChanged,
            )
          : [...capabilitiesWatcher, capabilitiesChanged],
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        },
      );
    };

    // RENDER ////////////////////////////////////////////////////////////////////

    return (
      <Controller
        name="capabilities"
        control={control}
        rules={capabilities}
        render={({ field }) => {
          // TODO: Should be set in SDK as a list prepared list
          const capabilityList = [
            EvseCapability.CHARGING_PROFILE_CAPABLE,
            EvseCapability.REMOTE_START_STOP_CAPABLE,
            EvseCapability.CREDIT_CARD_PAYABLE,
            EvseCapability.RESERVABLE,
            EvseCapability.RFID_READER,
            EvseCapability.UNLOCK_CAPABLE,
          ];

          return (
            <>
              {capabilityList.map((capabilityCurrent) => {
                return (
                  <Checkbox
                    key={capabilityCurrent}
                    id={capabilityCurrent}
                    label={t(
                      `delmonicos-shared-sdk.ocpi-protocol-enums.evse-capability.${capabilityCurrent}`,
                    )}
                    checked={
                      !field.value
                        ? false
                        : field.value.includes(capabilityCurrent)
                    }
                    onChange={() => handleCapibilitiesChange(capabilityCurrent)}
                  />
                );
              })}
            </>
          );
        }}
      />
    );
  };

const ChargePointCreateFormStep3InformationFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card flex={1}>
      <Stack direction="column" gap={GapEnum.L}>
        <AccentTitle
          as={HeadingEnum.H2}
          icon="info-circle"
          text={t('$.charge-point-create.step-3.title')}
          variant={AccentVariantEnum.Primary}
        />
        <Stack direction="column" gap={GapEnum.S}>
          <Text className={css.label}>
            {t('delmonicos-shared-sdk.outputs-channel.parking-restrictions')}
          </Text>
          <div className={css.checkboxesContainer}>
            <ChargePointCreateFormStep3ParkingRestrictionsSelection />
          </div>
        </Stack>
        <Stack direction="column" gap={GapEnum.S}>
          <Text className={css.label}>
            {t('delmonicos-shared-sdk.outputs-channel.capabilities')}
          </Text>
          <div className={css.checkboxesContainer}>
            <ChargePointCreateFormStep3CapabilitiesSelection />
          </div>
        </Stack>
      </Stack>
    </Card>
  );
};

const ChargePointCreateFormStep1TariffSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<CreateChargePointModel, 'tariffId'>;
  fieldState: ControllerFieldState;
}) => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { watch } = useFormContext<CreateChargePointModel>();
  const { tariffId } = useCreateChargePointFormValidation();
  const createChargePoint = useCreateChargePointContext();

  // WATCHERS ////////////////////////////////////////////////////////////////

  const customerId = watch('customerId');

  // SEARCH TERM ///////////////////////////////////////////////////////////////

  const [search, setSearch] = useState<string>('');

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const { data, isSuccess } = useCpmsBoGetTariffsPaginatedQuery(
    {
      limit: 10,
      offset: 0,
      name: search === '' ? undefined : `~${search}`,
    },
    {
      skip: !(customerId || createChargePoint.customerId),
    },
  );

  // AUTOCOMPLETE ITEM /////////////////////////////////////////////////////////

  const [selectedItem, setSelectedItem] = useState<AutocompleteItem | null>(
    null,
  );

  useEffect(() => {
    if (isSuccess) {
      const tariff =
        data.docs.find(
          ({ id }) => id === (field.value || createChargePoint.tariffId),
        ) ?? null;

      if (tariff) {
        setSelectedItem({ key: tariff.id, label: tariff.name });
        return;
      }

      setSelectedItem(null);
    }
  }, [search, data, isSuccess, field.value, createChargePoint.tariffId]);

  // AUTOCOMPLETE ITEMS ////////////////////////////////////////////////////////

  const [autocompleteItems, setAutocompleteItems] = useState<
    AutocompleteItem[]
  >([]);

  useEffect(() => {
    if (isSuccess) {
      setAutocompleteItems(
        data.docs.map(({ id, name }) => ({ key: id, label: name })) ?? [],
      );
    }
  }, [search, data, isSuccess]);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <AutocompleteInputComponent
      label={t('delmonicos-shared-sdk.outputs-gateway-charge-point.tariff')}
      placeholder={t(
        'delmonicos-shared-sdk.outputs-gateway-charge-point.tariff',
      )}
      required={!!tariffId.required}
      value={search}
      onChange={setSearch}
      onSearch={setSearch}
      items={autocompleteItems}
      selectedItem={selectedItem}
      onSelect={(selectedItemNext) => {
        field.onChange(
          selectedItemNext !== null ? selectedItemNext.key : undefined,
        );
      }}
      emptyResultText={t('global.no-result-found')}
      errorMessage={error?.message}
    />
  );
};

const ChargePointCreateFormStep3TariffFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control } = useFormContext<CreateChargePointModel>();
  const { tariffId } = useCreateChargePointFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card>
      <Stack direction="column" gap={GapEnum.L}>
        <AccentTitle
          as={HeadingEnum.H2}
          icon="info-circle"
          text={t('delmonicos-shared-sdk.outputs-gateway-charge-point.tariff')}
          variant={AccentVariantEnum.Primary}
        />
        <Controller
          name="tariffId"
          control={control}
          rules={tariffId}
          render={ChargePointCreateFormStep1TariffSelection}
        />
      </Stack>
    </Card>
  );
};

const ChargePointCreateFormStep1LocationSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<CreateChargePointModel, 'locationId'>;
  fieldState: ControllerFieldState;
}) => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { watch } = useFormContext<CreateChargePointModel>();
  const { locationId } = useCreateChargePointFormValidation();
  const createChargePoint = useCreateChargePointContext();

  // WATCHERS ////////////////////////////////////////////////////////////////

  const customerId = watch('customerId');
  const networkId = watch('networkId');

  // SEARCH TERM ///////////////////////////////////////////////////////////////

  const [search, setSearch] = useState<string>('');

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const { data, isSuccess } = useCpmsBoGetLocationsPaginatedQuery(
    {
      limit: 10,
      offset: 0,
      customerId: customerId || createChargePoint.customerId,
      networkId: networkId || createChargePoint.networkId,
      name: search === '' ? undefined : `~${search}`,
    },
    {
      skip:
        !(customerId || createChargePoint.customerId) &&
        !(networkId || createChargePoint.networkId),
    },
  );

  // AUTOCOMPLETE ITEM /////////////////////////////////////////////////////////

  const [selectedItem, setSelectedItem] = useState<AutocompleteItem | null>(
    null,
  );

  useEffect(() => {
    if (isSuccess) {
      const location =
        data.docs.find(
          ({ id }) => id === (field.value || createChargePoint.locationId),
        ) ?? null;

      if (location) {
        setSelectedItem({ key: location.id, label: location.name });
        return;
      }

      setSelectedItem(null);
    }
  }, [search, data, isSuccess, field.value, createChargePoint.locationId]);

  // AUTOCOMPLETE ITEMS ////////////////////////////////////////////////////////

  const [autocompleteItems, setAutocompleteItems] = useState<
    AutocompleteItem[]
  >([]);

  useEffect(() => {
    if (isSuccess) {
      setAutocompleteItems(
        data.docs.map(({ id, name }) => ({ key: id, label: name })) ?? [],
      );
    }
  }, [search, data, isSuccess]);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <AutocompleteInputComponent
      label={t('delmonicos-shared-sdk.outputs-gateway-charge-point.location')}
      placeholder={t(
        'delmonicos-shared-sdk.outputs-gateway-charge-point.location',
      )}
      required={!!locationId.required}
      value={search}
      onChange={setSearch}
      onSearch={setSearch}
      items={autocompleteItems}
      selectedItem={selectedItem}
      onSelect={(selectedItemNext) => {
        field.onChange(
          selectedItemNext === null ? undefined : selectedItemNext.key,
        );
      }}
      emptyResultText={t('global.no-result-found')}
      errorMessage={error?.message}
    />
  );
};

const ChargePointCreateFormStep3LocationFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control } = useFormContext<CreateChargePointModel>();
  const { locationId } = useCreateChargePointFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card>
      <Stack direction="column" gap={GapEnum.L}>
        <AccentTitle
          as={HeadingEnum.H2}
          icon="info-circle"
          text={t(
            'delmonicos-shared-sdk.outputs-gateway-charge-point.location',
          )}
          variant={AccentVariantEnum.Primary}
        />
        <Controller
          name="locationId"
          control={control}
          rules={locationId}
          render={ChargePointCreateFormStep1LocationSelection}
        />
      </Stack>
    </Card>
  );
};

export const ChargePointCreateFormStep3Controller = (): React.JSX.Element => {
  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack
      direction="row"
      justifyContent="stretch"
      alignItems="stretch"
      flex={1}
      gap={GapEnum.XL}>
      <Stack direction="column" flex={1} alignItems="stretch" gap={GapEnum.XL}>
        <ChargePointCreateFormStep3InformationFragment />
        <ChargePointCreateFormStep3TariffFragment />
      </Stack>
      <Stack direction="column" flex={1} alignItems="stretch" gap={GapEnum.XL}>
        <ChargePointCreateFormStep3LocationFragment />
      </Stack>
    </Stack>
  );
};
